import type { ReactNode } from 'react'
import { CopyButton } from '../common/CopyButton'
import { HelpIcon } from '../common/HelpIcon'
import s from './FieldGrid.module.scss'

/** Wie viele Spalten der Abschnitt auf dem Desktop hat. */
export type FieldColumns = 2 | 3 | 4

/**
 * Beschriftete Werte in Spalten.
 *
 * `<dl>` und nicht `<div>`: Das ist genau das, wofür eine Definitionsliste
 * gedacht ist — Begriff und zugehöriger Wert. Ein Bildschirmleser liest die
 * Paare dann als Paare vor.
 *
 * Die Spaltenzahl gilt nur auf dem Desktop. Tablet sind es immer zwei, Mobile
 * immer eine — drei Spalten auf 375 px wären 100 px je Wert.
 */
export function FieldGrid({ columns = 2, children }: {
  columns?: FieldColumns
  children: ReactNode
}) {
  return <dl className={s.grid} data-columns={columns}>{children}</dl>
}

interface FieldProps {
  label: string
  /** Fehlt der Wert, steht ein Gedankenstrich — nie eine leere Zelle. */
  value?: ReactNode
  help?: ReactNode
  /** Monospace mit gleich breiten Ziffern. Für ISIN, Beträge, Kennungen. */
  mono?: boolean
  /** Größer und dunkler. Für den einen Wert, um den es auf der Seite geht. */
  emphasis?: boolean
  /** Über die ganze Zeile. Für Freitext und lange Aufzählungen. */
  wide?: boolean
  /**
   * Der Wert zum Kopieren. Ist er gesetzt, erscheint hinter dem Wert ein Knopf.
   *
   * Ausdrücklich ein Text und kein `true`: `value` ist ein ReactNode und kann
   * eine Pille oder ein Datum sein — daraus liesse sich nicht ableiten, was in
   * der Zwischenablage landen soll. Bei einem Betrag ist das der Unterschied
   * zwischen `1.000,00` und `1000.00`.
   */
  copy?: string
}

export function Field({ label, value, help, mono, emphasis, wide,
                       copy }: FieldProps) {
  const empty = value == null || value === ''
  return (
    <div className={s.field} data-wide={wide}>
      <dt className={s.label}>
        {label}
        {help && <HelpIcon term={label}>{help}</HelpIcon>}
      </dt>
      <dd className={s.value} data-mono={mono} data-emphasis={emphasis}
          data-empty={empty}>
        {empty ? '—' : value}
        {/*
          Der Knopf steht DIREKT hinter dem Wert, nicht am rechten Rand des
          Feldes: Hier gibt es keine Spalte, an der etwas ausgerichtet werden
          müsste, und bei einem breiten Feld stünde er sonst weit weg von dem,
          was er kopiert. Bei fehlendem Wert entfällt er — ein Gedankenstrich
          ist nichts, was man weitergibt.
        */}
        {copy && !empty && <CopyButton value={copy} label={label} />}
      </dd>
    </div>
  )
}

/** Trennstrich innerhalb eines Abschnitts, wenn zwei Gruppen zusammengehören. */
export function FieldDivider() {
  return <div className={s.divider} />
}
